import classNames from "classnames";
import { useState } from "react";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import type { Segment } from "@helpers/segment";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./FeedbackPlayground.module.scss";

const segments: Segment[] = [
    {
        id: "income",
        label: "Income",
        value: 52,
        valueOverride: "52%",
        subtitle: "1,284 transactions",
        tooltip: "Income represents 52% of transactions",
        colors: {
            start: "#D8F3E5",
            end: "#A9E4C5",
            subtitle: "#267653",
        },
        subsegments: [
            {
                id: "sales",
                label: "Sales",
                value: 36,
                tooltip: "Sales income",
            },
            {
                id: "services",
                label: "Services",
                value: 16,
                tooltip: "Services income",
            },
        ],
    },
    {
        id: "expenses",
        label: "Expenses",
        value: 33,
        valueOverride: "33%",
        subtitle: "816 transactions",
        tooltip: "Expenses represent 33% of transactions",
        colors: {
            start: "#FDE7BD",
            end: "#F6CE80",
            subtitle: "#8A5A00",
        },
    },
    {
        id: "transfers",
        label: "Transfers",
        value: 15,
        valueOverride: "15%",
        subtitle: "371 transactions",
        tooltip: "Transfers represent 15% of transactions",
        colors: {
            start: "#DDE8FF",
            end: "#B9CEFA",
            subtitle: "#315DA8",
        },
    },
];

export const SegmentBarPlayground = () => {
    const [formatValueAsMoney, setFormatValueAsMoney] = useState(false);
    const [hasMinimumWidth, setHasMinimumWidth] = useState(true);
    const [showSubsegmentsOnHover, setShowSubsegmentsOnHover] = useState(true);
    const [selectedSegmentId, setSelectedSegmentId] = useState("None");

    return (
        <SandboxLayout
            controls={
                <section>
                    <h2>Features</h2>
                    <div className={styles.booleanGrid}>
                        <BooleanControl
                            label="Format values as money"
                            checked={formatValueAsMoney}
                            onChange={setFormatValueAsMoney}
                        />
                        <BooleanControl
                            label="Minimum segment width"
                            checked={hasMinimumWidth}
                            onChange={setHasMinimumWidth}
                        />
                        <BooleanControl
                            label="Subsegments on hover"
                            checked={showSubsegmentsOnHover}
                            onChange={setShowSubsegmentsOnHover}
                        />
                    </div>
                </section>
            }
        >
            <ShowcasePreview
                summary={`Selected: ${selectedSegmentId}`}
                output={JSON.stringify(
                    {
                        segments: segments.length,
                        formatValueAsMoney,
                        hasMinimumWidth,
                        showSubsegmentsOnHover,
                        showcaseSelectedSegmentId: selectedSegmentId,
                    },
                    null,
                    2,
                )}
            >
                <div className={classNames(styles.surface, styles.stack)}>
                    <SegmentBar
                        className={styles.segmentBar}
                        segments={segments}
                        formatValueAsMoney={formatValueAsMoney}
                        hasMinimumWidth={hasMinimumWidth}
                        showSubsegmentsOnHover={showSubsegmentsOnHover}
                        onSegmentClick={setSelectedSegmentId}
                    />
                    <ul className={styles.legend}>
                        {segments.map((segment) => (
                            <li className={styles.legendItem} key={segment.id}>
                                <span
                                    className={styles.legendSwatch}
                                    style={{
                                        backgroundColor: segment.colors.start,
                                    }}
                                />
                                {segment.label}: {segment.value}%
                            </li>
                        ))}
                    </ul>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
